/**
 * The registration step: a first-time client's registration with the studio,
 * the form the studio set (its title fits "Welcome to <studio>"), and the
 * client's agreement to the privacy policy, which the website draws itself.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'registration-step',
  description: "A first-time client's registration: the studio's registration form and the privacy policy agreement, then Continue.",
  drawing: 'standard-only',
  options: {},
  parts: {
    back: { description: 'Back to the offering.', required: false, states: ['disabled'] },
    title: { description: "The registration form's title.", required: true, role: 'title' },
    kind: { description: 'Registration · Required to book.', required: false },
    count: { description: 'The dots and "N of M forms", when the client owes two or more.', required: false },
    description: { description: "The form's description, when the studio wrote one.", required: false },
    fields: { description: "The form's fields.", required: true },
    consent: { description: 'The privacy policy agreement box.', required: true },
    policyLink: { description: 'The privacy policy, opened in a new tab.', required: true },
    consentError: { description: 'Shown when Continue is pressed without the box ticked.', required: true },
    submit: { description: 'Continue (or Complete Registration while fields are missing).', required: true, states: ['disabled'], role: 'primary-action' },
    submitError: { description: 'The line above the button when the answers could not be sent and no field says why.', required: false },
    failed: { description: "A form that could not be read, in the app's words, with Go Back.", required: true },
  },
  actions: {
    setConsent: { description: 'Ticks or clears the privacy policy agreement.', args: [{ name: 'agreed', kind: 'value', type: 'boolean' }] },
    back: { description: 'Goes back to the offering, releasing the held time.', args: [] },
  },
  events: {
    registered: 'The client registered with the studio.',
  },
});

/** What the registration step knows. */
export interface RegistrationStepState {
  /** loading until the form is read; failed when it could not be. */
  readonly status: 'loading' | 'ready' | 'failed';
  /** True once the privacy policy box is ticked. */
  readonly consented: boolean;
  /** True after Continue was pressed with the box clear. */
  readonly consentError: boolean;
  /** Where the privacy policy opens. */
  readonly policyHref: string;
}

/** What a drawing can ask the registration step to do. */
export interface RegistrationStepActions {
  /** Tick (true) or clear (false) the privacy policy agreement. */
  readonly setConsent: (agreed: boolean) => void;
  /** Go back to the offering, releasing the held time. */
  readonly back: () => void;
}

/** What the registration step tells its host. */
export interface RegistrationStepEvents {
  /** The client registered with the studio. */
  readonly registered: Record<string, never>;
}
